import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { api } from '../services/api';

interface User {
  id: string;
  telegramId: number;
  username?: string;
  firstName?: string;
  lastName?: string;
  theme: 'light' | 'dark';
}

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  initAuth: (initData: string) => Promise<void>;
  logout: () => void;
  updateTheme: (theme: 'light' | 'dark') => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,

      initAuth: async (initData: string) => {
        try {
          const response = await api.post('/auth/telegram', { initData });
          const { token, user } = response.data;

          set({
            token,
            user: {
              ...user,
              theme: (user.theme as 'light' | 'dark') || 'light',
            },
            isAuthenticated: true,
          });

          // Set token in API client
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        } catch (error) {
          console.error('Auth failed:', error);
          set({ isAuthenticated: false, user: null, token: null });
        }
      },

      logout: () => {
        set({ isAuthenticated: false, user: null, token: null });
        delete api.defaults.headers.common['Authorization'];
      },

      updateTheme: async (theme: 'light' | 'dark') => {
        const currentUser = get().user;
        if (!currentUser) return;

        try {
          await api.patch('/users/me', { theme });
          set({
            user: { ...currentUser, theme },
          });
        } catch (error) {
          console.error('Failed to update theme:', error);
        }
      },
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        token: state.token,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);

